import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import './App.css';
import YearSelector from './components/year-selector.js';
import StackedBarChart from './components/hs-chart.js';
//import chartData from './data/crm-sales.json';
import StatsBar from './components/stats-bar.js';
import DealStats from './components/deal-stats';
import FiveWins from './components/five-wins';
import UserHeader from './components/UserHeader';
import { apiRequest } from './authConfig';
import styled from 'styled-components'


function App() {
  const { accounts, instance } = useMsal();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [year, setYear] = useState('2026');
  const [modal, setModal] = useState({ open: false, title: '', deals: [], totalAmount: 0 });
  const [userInfo, setUserInfo] = useState(null);

  // Get user info from MSAL account
  useEffect(() => {
    if (accounts.length > 0) {
      setUserInfo({
        name: accounts[0].name,
        email: accounts[0].username
      });
    }
  }, [accounts]);

  useEffect(() => {
    if (!accounts.length) return; // Don't fetch data if not authenticated
    
    setIsLoading(true);
    setError(null);
    const fetchData = async () => {
      try {
        // Get access token for API calls
        const tokenResponse = await instance.acquireTokenSilent({
          scopes: apiRequest.scopes,
          account: accounts[0]
        });

        // REACT_APP_API_BASE_URL 
        //const response = await fetch('http://localhost:7071/api/hubspotChart', {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/hubspotChart`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${tokenResponse.accessToken}`
          },
          body: JSON.stringify({ year: year }),
        });
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const result = await response.json();
        setData(result);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [year, accounts, instance]);
  const hasData = !!data;

  const handleSegmentClick = ({ month, stageKey, stageLabel }) => {
    const list = (data.dealsLookup?.[month]?.[stageKey]) || [];
    const totalAmount = list.reduce((sum, d) => sum + (Number(d.amount) || 0), 0);
    setModal({
      open: true,
      title: `${stageLabel} - ${month}`,
      deals: list,
      totalAmount
    });
  };

  const closeModal = () => setModal({ open: false, title: '', deals: [] });

  return (
    <div className="App">
      <UserHeader userInfo={userInfo} />
      <header className="App-header">
        <Container>
          <YearSelector year={year} setYear={setYear} />
          {error && (<ErrorBanner>Error: {error}</ErrorBanner>)}
          <StatsBar data={hasData ? data.chartData : null} loading={isLoading} />
          <StackedBarChart data={hasData ? data.chartData : null} loading={isLoading} onSegmentClick={handleSegmentClick} />
          <DealStats data={hasData ? data.stats : null} loading={isLoading} />
          <FiveWins data={hasData ? data.stats : null} loading={isLoading} />
          {modal.open && (
            <ModalOverlay onClick={closeModal}>
              <ModalBox onClick={(e) => e.stopPropagation()}>
                <ModalHeader>
                  <HeaderText>
                    <ModalTitle>{modal.title}</ModalTitle>
                    <ModalSubTitle>
                      {modal.deals.length} {modal.deals.length === 1 ? 'deal' : 'deals'} • Total: {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(modal.totalAmount)}
                    </ModalSubTitle>
                  </HeaderText>
                  <CloseButton onClick={closeModal}>×</CloseButton>
                </ModalHeader>
                <ModalBody>
                  {modal.deals.length === 0 ? (
                    <EmptyState>No deals.</EmptyState>
                  ) : (
                    modal.deals.map((d) => {
                      const url = `https://app-ap1.hubspot.com/contacts/39891391/record/0-3/${d.id}`;
                      const amount = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(d.amount || 0);
                      return (
                        <DealRow key={d.id} onClick={() => window.open(url, '_blank')}>
                          <DealName href={url} target="_blank" rel="noreferrer">{d.dealname}</DealName>
                          <DealAmount>{amount}</DealAmount>
                        </DealRow>
                      );
                    })
                  )}
                </ModalBody>
              </ModalBox>
            </ModalOverlay>
          )}
        </Container>
      </header>
    </div>
  );
}

export default App;

const Container = styled.div`
  width: 100%;
  max-width: 1200px;
  height: 100%;
`;

const ErrorBanner = styled.div`
  margin: 12px 0 0 0;
  padding: 10px 14px;
  border-radius: 8px;
  background: #fff4f4;
  color: #b42318;
  border: 1px solid #ffd2d2;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalBox = styled.div`
  width: min(720px, 92vw);
  max-height: 78vh;
  overflow: auto;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(0,0,0,0.22);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-bottom: 1px solid #eef1f5;
`;

const CloseButton = styled.button`
  font-size: 20px;
  line-height: 20px;
  background: transparent;
  border: none;
  cursor: pointer;
`;

const ModalBody = styled.div`
  padding: 8px 12px 16px 20px;
`;

const DealRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 12px 12px 12px;
  margin: 8px;
  border-radius: 10px;
  background: #f9fafb;
  border: 1px solid #eef1f5;
  transition: background .15s ease, transform .05s ease;
  will-change: background, transform;
  cursor: pointer;
  &:hover { background: #f3f6fb; }
`;

const DealName = styled.a`
  color: #193b7a;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.95rem;
  padding-right: 12px;
`;

const DealAmount = styled.div`
  color: #0b1220;
  font-weight: 600;
  font-size: 0.95rem;
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
`;

const ModalTitle = styled.div`
  font-size: 1.18rem;
  font-weight: 700;
  color: #0b1220;
`;

const ModalSubTitle = styled.div`
  margin-top: 2px;
  font-size: 0.8rem;
  color: #6a7380;
  text-align: left;
`;

const EmptyState = styled.div`
  padding: 18px;
  color: #6a7380;
  font-size: 0.9rem;
`;

 